import {
  type CSSProperties,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/** Returns `value` after it stayed unchanged for `delayMs`. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** `localStorage` with try/catch: private windows and blocked storage must never break the UI. */
export function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage is a convenience only.
  }
}

/**
 * Tracks a media query. The first render already reads `matchMedia` (no flash of the wrong
 * layout); without `matchMedia` (old test environments) the query counts as not matching.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window.matchMedia !== "function") return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", notify);
      return () => list.removeEventListener("change", notify);
    },
    [query],
  );
  const getSnapshot = () =>
    typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false;
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Horizontal overflow state of a scroll container: whether content is hidden at the start or end.
 * Spread `props` on the element (`onScroll`, `data-overflow-*`, the `--fade` mask variable); the
 * element still needs the classes `[mask-image:var(--fade)] [-webkit-mask-image:var(--fade)]`.
 * Pass `deps` that change when the content changes, and `watch` for a value (e.g. the path) that
 * should scroll the `[aria-current="page"]` entry into view.
 */
export function useOverflowEdges<T extends HTMLElement>(
  contentKey: unknown,
  scrollCurrentKey?: unknown,
) {
  const ref = useRef<T>(null);
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.scrollLeft > 1;
    const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflow((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the content changes
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, contentKey]);

  useEffect(() => {
    if (scrollCurrentKey === undefined) return;
    ref.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
    measure();
  }, [scrollCurrentKey, measure]);

  const fade = (edge: "start" | "end") => (overflow[edge] ? "transparent" : "black");
  const mask = `linear-gradient(to right, ${fade("start")}, black 2rem, black calc(100% - 2rem), ${fade("end")})`;
  const props = {
    "data-overflow-start": overflow.start,
    "data-overflow-end": overflow.end,
    onScroll: measure,
    style: overflow.start || overflow.end ? ({ "--fade": mask } as CSSProperties) : undefined,
  };
  return { ref: ref as RefObject<T | null>, props };
}
